import { useState, useEffect } from 'react';
import type { MonthlyPackage } from '../types';

interface MonthlyPackageModalProps {
  isOpen: boolean;
  package: MonthlyPackage | null;
  existingPackages: MonthlyPackage[];
  onClose: () => void;
  onSave: (packageData: Partial<MonthlyPackage>) => void;
}

export default function MonthlyPackageModal({
  isOpen,
  package: pkg,
  existingPackages,
  onClose,
  onSave,
}: MonthlyPackageModalProps) {
  const [formData, setFormData] = useState<Partial<MonthlyPackage>>({
    name: '',
    duration: 0,
    timeSlot: { type: 'unlimited' },
    price: 0,
    sort: 999,
    isEnabled: true,
  });

  const [error, setError] = useState('');

  useEffect(() => {
    if (pkg) {
      setFormData(pkg);
    } else {
      setFormData({
        name: '',
        duration: 0,
        timeSlot: { type: 'unlimited' },
        price: 0,
        sort: 999,
        isEnabled: true,
      });
    }
    setError('');
  }, [pkg, isOpen]);

  const handleDurationChange = (duration: number) => {
    setFormData({
      ...formData,
      duration,
      name: duration === 0 ? '不限时长' : `${duration}小时`,
    });
  };

  const handleSave = () => {
    if (!formData.name) {
      setError('请输入套餐名称');
      return;
    }

    if (formData.duration === undefined || formData.duration < 0) {
      setError('请输入有效的时长');
      return;
    }

    if (!formData.price || formData.price <= 0) {
      setError('请输入有效的价格');
      return;
    }

    // 检查时长是否重复
    const isDuplicate = existingPackages.some(
      (p) => p.duration === formData.duration && (!pkg || p.id !== pkg.id)
    );
    if (isDuplicate) {
      setError('该时长的套餐已存在，请勿重复添加');
      return;
    }

    // 检查价格是否超过不限时长套餐价格
    const unlimitedPackage = existingPackages.find((p) => p.duration === 0);
    if (
      unlimitedPackage &&
      formData.duration !== 0 &&
      formData.price &&
      formData.price > unlimitedPackage.price
    ) {
      setError('价格不能超过不限时长套餐的价格');
      return;
    }

    if (formData.timeSlot?.type === 'limited') {
      if (!formData.timeSlot.start || !formData.timeSlot.end) {
        setError('请输入完整的时段范围');
        return;
      }
    }

    onSave(formData);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center overflow-y-auto">
      <div className="absolute inset-0 bg-black/50" onClick={onClose}></div>
      <div className="relative bg-white rounded-lg shadow-xl w-full max-w-2xl mx-4 my-8">
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h3 className="text-xl font-semibold text-gray-900">
            {pkg ? '编辑套餐' : '添加套餐'}
          </h3>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 transition-colors"
          >
            <i className="ri-close-line text-xl"></i>
          </button>
        </div>

        <div className="p-6 space-y-4">
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
              {error}
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              时长 <span className="text-red-500">*</span>
            </label>
            <div className="flex gap-4 mb-2">
              <label className="flex items-center cursor-pointer">
                <input
                  type="radio"
                  checked={formData.duration === 0}
                  onChange={() => handleDurationChange(0)}
                  className="w-4 h-4 text-teal-500 border-gray-300 focus:ring-teal-500 cursor-pointer"
                />
                <span className="ml-2 text-sm text-gray-700">不限时长</span>
              </label>
              <label className="flex items-center cursor-pointer">
                <input
                  type="radio"
                  checked={formData.duration !== 0}
                  onChange={() => handleDurationChange(1)}
                  className="w-4 h-4 text-teal-500 border-gray-300 focus:ring-teal-500 cursor-pointer"
                />
                <span className="ml-2 text-sm text-gray-700">限制时长</span>
              </label>
            </div>
            {formData.duration !== 0 && (
              <input
                type="number"
                value={formData.duration}
                onChange={(e) => handleDurationChange(Number(e.target.value))}
                placeholder="请输入时长（小时）"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
              />
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              套餐名称 <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="自动生成或手动输入"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              时段 <span className="text-red-500">*</span>
            </label>
            <div className="space-y-3">
              <label className="flex items-center cursor-pointer">
                <input
                  type="radio"
                  checked={formData.timeSlot?.type === 'unlimited'}
                  onChange={() => setFormData({ ...formData, timeSlot: { type: 'unlimited' } })}
                  className="w-4 h-4 text-teal-500 border-gray-300 focus:ring-teal-500 cursor-pointer"
                />
                <span className="ml-2 text-sm text-gray-700">不限时段</span>
              </label>
              <label className="flex items-center cursor-pointer">
                <input
                  type="radio"
                  checked={formData.timeSlot?.type === 'limited'}
                  onChange={() =>
                    setFormData({ ...formData, timeSlot: { type: 'limited', start: '', end: '' } })
                  }
                  className="w-4 h-4 text-teal-500 border-gray-300 focus:ring-teal-500 cursor-pointer"
                />
                <span className="ml-2 text-sm text-gray-700">输入时段</span>
              </label>
              {formData.timeSlot?.type === 'limited' && (
                <div className="ml-6 grid grid-cols-2 gap-4">
                  <input
                    type="time"
                    value={formData.timeSlot.start}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        timeSlot: { ...formData.timeSlot!, start: e.target.value },
                      })
                    }
                    className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                  />
                  <input
                    type="time"
                    value={formData.timeSlot.end}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        timeSlot: { ...formData.timeSlot!, end: e.target.value },
                      })
                    }
                    className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                  />
                </div>
              )}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              价格 (元) <span className="text-red-500">*</span>
            </label>
            <input
              type="number"
              step="0.01"
              value={formData.price}
              onChange={(e) => setFormData({ ...formData, price: Number(e.target.value) })}
              placeholder="请输入价格"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              排序 <span className="text-red-500">*</span>
            </label>
            <input
              type="number"
              value={formData.sort}
              onChange={(e) => setFormData({ ...formData, sort: Number(e.target.value) })}
              placeholder="数字越小越靠前"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
            />
          </div>
        </div>

        <div className="flex justify-end gap-3 px-6 pb-6 border-t border-gray-200 pt-4">
          <button
            onClick={onClose}
            className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors whitespace-nowrap"
          >
            取消
          </button>
          <button
            onClick={handleSave}
            className="px-6 py-2 bg-teal-500 text-white rounded-lg hover:bg-teal-600 transition-colors whitespace-nowrap"
          >
            保存
          </button>
        </div>
      </div>
    </div>
  );
}
